import React from "react";
import { CartesianGrid, Cell, ReferenceArea, ReferenceLine, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis, ZAxis } from "recharts";
import { Card, CardContent } from "../../ui/card";
import { Headline, Kpi, PainBanner, Pill, SectionTitle } from "./PersonaUi";
import { GRADE_EVIDENCE, OBS, funds } from "./allocatorData";

const TT = { background: "#0b0b0f", border: "1px solid #27272a", fontSize: 11 };
const col = (f) => (f.indexLike ? "#fbbf24" : ["A", "B"].includes(f.letter) ? "#34d399" : ["D", "F"].includes(f.letter) ? "#f87171" : "#a1a1aa");
const label = (f) => (f.indexLike ? ["INDEX-LIKE", "alert"] : f.letter ? [`GRADE ${f.letter}`, ["A", "B"].includes(f.letter) ? "good" : ["D", "F"].includes(f.letter) ? "bad" : "muted"] : ["UNGRADED", "muted"]);

export default function AllocFees() {
  const F = funds().sort((a, b) => b.aer - a.aer);
  const idx = F.filter((f) => f.indexLike);
  const aum = idx.reduce((s, f) => s + f.aum, 0) / 1e9;
  const data = F.map((f) => ({ ...f, x: f.active_share * 100, y: f.er, z: f.aum / 1e9 }));
  return (
    <div className="space-y-4" data-testid="persona-alloc-fees">
      <PainBanner testid="persona-alloc-fees-pain" persona="LONG-ONLY ALLOCATOR · FEE CHECK"
        pain="Am I paying active fees for a portfolio that mostly hugs the index?"
        answer="active share vs fee, with the real cost of the active part" />
      <div>
        <Headline testid="alloc-fees-headline">{idx.length} of {F.length} large-cap funds hug a benchmark — {aum >= 1000 ? `$${(aum / 1000).toFixed(1)}T` : `$${Math.round(aum)}B`} of assets paying active fees for under 80% active share</Headline>
        <div className="text-sm text-muted-foreground mt-1">Active share from N-PORT holdings vs the closest of three cap-segment benchmarks, rolling 3 months · grades as of {OBS}</div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testid="alloc-fees-kpi-index" label="Index-like funds" value={`${idx.length} / ${F.length}`} tone="text-amber-400" sub="active share < 80%" />
        <Kpi testid="alloc-fees-kpi-aer" label="Highest active-fee" value={`${F[0].aer.toFixed(2)}%`} sub={`${F[0].fund} · pays for the active part only`} />
        <Kpi testid="alloc-fees-kpi-graded" label="Truly active & graded A/B" value={F.filter((f) => !f.indexLike && ["A", "B"].includes(f.letter)).length} tone="text-emerald-400" sub="positioning grade top tiers" />
        <Kpi testid="alloc-fees-kpi-bottom" label="Active but grade F" value={F.filter((f) => f.letter === "F").length} tone="text-red-400" sub="different, but not in the right names" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 items-start">
        <Card className="xl:col-span-3"><CardContent className="pt-4">
          <SectionTitle>Active share vs expense ratio (bubble = AUM)</SectionTitle>
          <div className="h-[360px]" data-testid="alloc-fees-scatter">
            <ResponsiveContainer>
              <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <ReferenceArea x1={65} x2={80} y1={0.25} y2={1} fill="#fbbf24" fillOpacity={0.05}
                  label={{ value: "PAYING ACTIVE, GETTING INDEX", position: "insideTopLeft", fontSize: 10, fill: "#fbbf24" }} />
                <XAxis type="number" dataKey="x" domain={[65, 100]} ticks={[65, 70, 75, 80, 85, 90, 95, 100]} tick={{ fontSize: 10, fill: "#71717a" }} tickFormatter={(v) => `${v}%`}
                  label={{ value: "Active share", position: "bottom", offset: 8, fontSize: 10, fill: "#a1a1aa" }} />
                <YAxis type="number" dataKey="y" domain={[0.25, 1]} tick={{ fontSize: 10, fill: "#71717a" }} tickFormatter={(v) => `${v.toFixed(2)}%`}
                  label={{ value: "Expense ratio", angle: -90, position: "insideLeft", fontSize: 10, fill: "#a1a1aa" }} />
                <ZAxis type="number" dataKey="z" range={[60, 600]} />
                <ReferenceLine x={80} stroke="#fbbf24" strokeDasharray="4 3" />
                <Tooltip contentStyle={TT} formatter={(v, n) => [n === "x" ? `${v.toFixed(1)}%` : n === "y" ? `${v.toFixed(2)}%` : `$${v.toFixed(0)}B`, n === "x" ? "active share" : n === "y" ? "expense ratio" : "AUM"]}
                  labelFormatter={() => ""} />
                <Scatter data={data} label={{ dataKey: "fund", position: "top", fontSize: 9, fill: "#d4d4d8" }}>
                  {data.map((d) => <Cell key={d.fund} fill={col(d)} fillOpacity={0.85} />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </CardContent></Card>
        <Card className="xl:col-span-2"><CardContent className="pt-4">
          <SectionTitle>Cost of the active part (active expense ratio)</SectionTitle>
          <table className="w-full text-xs" data-testid="alloc-fees-table">
            <thead className="border-b border-border text-muted-foreground"><tr>
              <th className="text-left py-1.5 px-1.5">Fund</th><th className="text-right px-1.5">AS</th><th className="text-right px-1.5">ER</th><th className="text-right px-1.5">Active ER</th><th className="text-right px-1.5">Status</th>
            </tr></thead>
            <tbody>
              {F.map((f) => (
                <tr key={f.fund} className="border-b border-border/40" data-testid={`alloc-fees-row-${f.fund}`}>
                  <td className="py-1.5 px-1.5"><span className="font-mono font-bold">{f.fund}</span> <span className="text-[10px] text-muted-foreground">{f.short}</span></td>
                  <td className={`text-right px-1.5 font-mono ${f.indexLike ? "text-amber-400" : ""}`}>{(f.active_share * 100).toFixed(1)}%</td>
                  <td className="text-right px-1.5 font-mono">{f.er.toFixed(2)}%</td>
                  <td className="text-right px-1.5 font-mono font-bold">{f.aer.toFixed(2)}%</td>
                  <td className="text-right px-1.5"><Pill tone={label(f)[1]}>{label(f)[0]}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>
      </div>
      <div className="text-[10px] text-muted-foreground">
        Active expense ratio = (ER − (1 − active share) × 0.03% index fee) ÷ active share — what you really pay for the non-index part. Active share, grades, AUM: real
        N-PORT-based platform data ({OBS}). Expense ratios: approximate public figures for the listed share class (mockup). {GRADE_EVIDENCE}
      </div>
    </div>
  );
}
